import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, HelpCircle, Mic, MicOff, Sparkles, User, Loader2, Volume2 } from 'lucide-react';
import { useAppState } from '../../contexts/AppContext';
import { GeminiLiveSession, AppContext as AIContext } from '../../services/geminiLiveService';
import { AudioCapture, AudioPlayer } from '../../utils/audioUtils';

type State = 'connecting' | 'ready' | 'listening' | 'processing' | 'speaking' | 'error';

interface ChatMessage {
    role: 'user' | 'assistant';
    text: string;
    actions?: string[];
}

export default function AppVoiceCapture() {
    const navigate = useNavigate();
    const appState = useAppState();

    const [state, setState] = useState<State>('connecting');
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    const [showTips, setShowTips] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const sessionRef = useRef<GeminiLiveSession | null>(null);
    const captureRef = useRef<AudioCapture | null>(null);
    const playerRef = useRef<AudioPlayer | null>(null);
    const isRecordingRef = useRef(false);

    // Initialize session on mount
    useEffect(() => {
        const init = async () => {
            try {
                const session = new GeminiLiveSession();
                const player = new AudioPlayer();

                // Build context
                const now = new Date();
                const context: AIContext = {
                    projects: appState.projects,
                    tasks: appState.tasks,
                    events: appState.events,
                    inboxItems: appState.inboxItems,
                    currentDate: now.toISOString().split('T')[0],
                    currentTime: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                };

                // Set up callbacks
                session.onAudioResponse((audioData) => {
                    player.play(audioData);
                    setState('speaking');
                });

                session.onTextResponse((text) => {
                    setChatHistory(prev => {
                        const last = prev[prev.length - 1];
                        if (last && last.role === 'assistant') {
                            return [...prev.slice(0, -1), { ...last, text: last.text + ' ' + text }];
                        }
                        return [...prev, { role: 'assistant', text, actions: [] }];
                    });
                });

                session.onFunctionCall(async (call) => {
                    const result = await executeFunction(call);
                    return result;
                });

                session.onStateChange((connectionState) => {
                    if (connectionState === 'connected') {
                        setState('ready');
                    } else if (connectionState === 'error') {
                        setState('error');
                        setErrorMessage('Connection failed. Check API key.');
                    }
                });

                // When playback ends, go back to ready
                player.setOnPlaybackEnd(() => {
                    setState('ready');
                });

                // Context provider for persistent memory
                const contextProvider = () => context;

                // Connect
                await session.connect(context, contextProvider);

                sessionRef.current = session;
                playerRef.current = player;

            } catch (error) {
                console.error('Init failed:', error);
                setState('error');
                setErrorMessage(error instanceof Error ? error.message : 'Failed to connect');
            }
        };

        init();

        return () => {
            sessionRef.current?.disconnect();
            captureRef.current?.stop();
            playerRef.current?.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Execute function calls
    const executeFunction = useCallback(async (call: any): Promise<string> => {
        const name = call.name;
        const args = call.args || {};

        try {
            let result = '';

            switch (name) {
                // Projects
                case 'createProject':
                    appState.addProject({
                        name: args.name,
                        color: args.color || '#2A7A85',
                        bgColor: args.bgColor || '#D9EEF2',
                        icon: args.icon || '📁',
                    });
                    result = `Created project: ${args.name}`;
                    break;

                case 'listProjects':
                    result = appState.projects.length > 0
                        ? `Projects: ${appState.projects.map(p => p.name).join(', ')}`
                        : 'No projects';
                    break;

                case 'deleteProject':
                    appState.deleteProject(args.id);
                    result = 'Deleted project';
                    break;

                // Tasks
                case 'createTask':
                    appState.addTask({
                        title: args.title,
                        completed: false,
                        priority: args.priority || 'medium',
                        dueDate: args.dueDate,
                        timeStart: args.timeStart,
                        projectId: args.projectId,
                        context: 'Voice',
                        contextColor: '#0D5F6B',
                        contextBgColor: '#D9EEF2',
                        subtasks: [],
                    });
                    result = `Created task: ${args.title}`;
                    break;

                case 'listTasks':
                    const tasks = appState.tasks.filter(t => {
                        if (args.completed !== undefined && t.completed !== args.completed) return false;
                        if (args.projectId && t.projectId !== args.projectId) return false;
                        return true;
                    });
                    result = tasks.length > 0
                        ? `Tasks: ${tasks.slice(0, 5).map(t => t.title).join(', ')}`
                        : 'No tasks found';
                    break;

                case 'updateTask':
                    appState.updateTask(args.id, args);
                    result = 'Updated task';
                    break;

                case 'deleteTask':
                    appState.deleteTask(args.id);
                    result = 'Deleted task';
                    break;

                case 'completeTask':
                    appState.toggleTaskComplete(args.id);
                    result = 'Marked task complete';
                    break;

                // Events
                case 'createEvent':
                    await appState.addEvent({
                        title: args.title,
                        date: args.date,
                        timeStart: args.timeStart,
                        timeEnd: args.timeEnd,
                        type: 'meeting',
                        color: '#8B9FD8',
                        location: args.location,
                    });
                    result = `Created event: ${args.title}`;
                    break;

                case 'listEvents':
                    const events = appState.events.filter(e => e.date && e.date >= new Date().toISOString().split('T')[0]);
                    result = events.length > 0
                        ? `Events: ${events.slice(0, 5).map(e => `${e.title} on ${e.date}`).join(', ')}`
                        : 'No upcoming events';
                    break;

                case 'deleteEvent':
                    await appState.deleteEvent(args.id);
                    result = 'Deleted event';
                    break;

                default:
                    result = `Unknown function: ${name}`;
            }

            // Add action to chat
            setChatHistory(prev => {
                const last = prev[prev.length - 1];
                if (last && last.role === 'assistant') {
                    const actions = last.actions || [];
                    return [...prev.slice(0, -1), { ...last, actions: [...actions, `✓ ${result}`] }];
                }
                return prev;
            });

            return result;
        } catch (error) {
            console.error('Function error:', error);
            return `Error: ${name} failed`;
        }
    }, [appState]);

    // Handle mic button
    const handleMicClick = async () => {
        if (state === 'error' || state === 'connecting') return;

        if (!isRecordingRef.current) {
            // Start recording
            try {
                const capture = new AudioCapture();

                await capture.start((audioData) => {
                    sessionRef.current?.sendAudio(audioData);
                });

                captureRef.current = capture;
                isRecordingRef.current = true;
                setState('listening');

                // Add user message placeholder
                setChatHistory(prev => [...prev, { role: 'user', text: '🎤 Speaking...' }]);

            } catch (error) {
                console.error('Mic error:', error);
                setErrorMessage('Microphone access denied');
                setState('error');
            }
        } else {
            // Stop recording
            captureRef.current?.stop();
            captureRef.current = null;
            isRecordingRef.current = false;

            // Signal end of turn
            sessionRef.current?.endTurn();

            setState('processing');

            // Update user message
            setChatHistory(prev => {
                const updated = [...prev];
                if (updated.length > 0 && updated[updated.length - 1].role === 'user') {
                    updated[updated.length - 1] = { role: 'user', text: '🎤 Spoke to assistant' };
                }
                return updated;
            });
        }
    };

    const handleClose = () => {
        sessionRef.current?.disconnect();
        captureRef.current?.stop();
        playerRef.current?.stop();
        navigate('/app');
    };

    const getStateText = () => {
        switch (state) {
            case 'connecting': return 'Connecting...';
            case 'ready': return 'Ready';
            case 'listening': return 'Listening...';
            case 'processing': return 'Processing...';
            case 'speaking': return 'Speaking...';
            case 'error': return 'Error';
        }
    };

    const getMicColor = () => {
        if (state === 'listening') return 'bg-red-500';
        if (state === 'processing' || state === 'speaking') return 'bg-gray-400';
        if (state === 'error') return 'bg-red-500';
        return 'bg-[#0D5F6B]';
    };

    return (
        <div className="min-h-screen bg-[#F8FAFB] flex flex-col">
            {/* Header */}
            <div className="bg-white shadow-sm">
                <div className="h-[env(safe-area-inset-top,0px)]"></div>
                <header className="h-14 flex justify-between items-center px-4">
                    <button onClick={handleClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                    <span className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        {state === 'speaking' ? <Volume2 className="w-4 h-4 text-[#0D5F6B] animate-pulse" /> : <Sparkles className="w-4 h-4 text-[#0D5F6B]" />}
                        {getStateText()}
                    </span>
                    <button onClick={() => setShowTips(true)} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
                        <HelpCircle className="w-5 h-5 text-gray-600" />
                    </button>
                </header>
            </div>

            {/* Chat */}
            <main className="flex-1 flex flex-col px-4 py-6 overflow-hidden">
                <div className="flex-1 overflow-y-auto mb-4 space-y-3">
                    {chatHistory.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <Sparkles className="w-16 h-16 text-[#0D5F6B] mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Assistant</h3>
                            <p className="text-sm text-gray-500 max-w-xs">
                                Tap the microphone and speak naturally. I can manage your tasks, calendar, and projects!
                            </p>
                        </div>
                    ) : (
                        chatHistory.map((msg, i) => (
                            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                {msg.role === 'assistant' && (
                                    <div className="w-8 h-8 rounded-full bg-[#0D5F6B] flex items-center justify-center flex-shrink-0">
                                        <Sparkles className="w-4 h-4 text-white" />
                                    </div>
                                )}
                                <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-[#0D5F6B] text-white' : 'bg-white shadow-sm'}`}>
                                    <p className={`text-sm ${msg.role === 'user' ? 'text-white' : 'text-gray-900'}`}>
                                        {msg.text}
                                    </p>
                                    {msg.actions && msg.actions.length > 0 && (
                                        <div className="mt-2 space-y-1">
                                            {msg.actions.map((action, j) => (
                                                <p key={j} className="text-xs text-green-600 font-medium">{action}</p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {msg.role === 'user' && (
                                    <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0">
                                        <User className="w-4 h-4 text-white" />
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Error */}
                {errorMessage && (
                    <div className="mb-4 bg-red-100 rounded-xl p-3">
                        <p className="text-sm text-red-600 text-center">{errorMessage}</p>
                    </div>
                )}

                {/* Mic Button */}
                <div className="flex flex-col items-center">
                    <button
                        onClick={handleMicClick}
                        disabled={state === 'connecting' || state === 'processing' || state === 'speaking'}
                        className={`w-16 h-16 rounded-full flex items-center justify-center transition-all shadow-lg ${getMicColor()} disabled:opacity-50`}
                    >
                        {state === 'connecting' ? (
                            <Loader2 className="w-7 h-7 text-white animate-spin" />
                        ) : state === 'listening' ? (
                            <MicOff className="w-7 h-7 text-white" />
                        ) : state === 'speaking' ? (
                            <Volume2 className="w-7 h-7 text-white animate-pulse" />
                        ) : state === 'processing' ? (
                            <Loader2 className="w-7 h-7 text-white animate-spin" />
                        ) : (
                            <Mic className="w-7 h-7 text-white" />
                        )}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                        {state === 'listening' ? 'Tap to stop' : state === 'ready' ? 'Tap to speak' : ''}
                    </p>
                </div>
            </main>

            {/* Tips */}
            {showTips && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
                    <div className="bg-white w-full rounded-t-2xl p-6 pb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Tips</h2>
                            <button onClick={() => setShowTips(false)} className="w-8 h-8 flex items-center justify-center">
                                <X className="w-5 h-5 text-gray-400" />
                            </button>
                        </div>
                        <div className="space-y-3 text-sm text-gray-600">
                            <p>• Speak naturally - I understand conversational language</p>
                            <p>• Tap the mic to start, tap again when done</p>
                            <p>• I can create, update, and delete tasks and events</p>
                            <div className="mt-4 space-y-2">
                                <p className="text-[#0D5F6B] font-semibold">Try:</p>
                                <p className="text-[#0D5F6B]">"What's on my schedule today?"</p>
                                <p className="text-[#0D5F6B]">"Create a task to call John tomorrow"</p>
                                <p className="text-[#0D5F6B]">"Schedule a meeting for Friday at 2pm"</p>
                            </div>
                        </div>
                        <button onClick={() => setShowTips(false)} className="w-full mt-6 py-3 rounded-xl bg-[#0D5F6B] text-white font-semibold">
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
